import { MessageSource, FollowUpAction } from '../types/chat';
import { SystemNote } from '../types/systemNote';

export interface BotReply {
    content: string;
    sources: MessageSource[];
    confidenceLevel?: number; // 0-100
    followUpActions?: FollowUpAction[];
    humanNotified?: boolean; // Indicates if a human has been notified
    notifiedPerson?: string; // Name of the person who was notified
    systemNotesUsed?: SystemNote[]; // System notes that were referenced
}

/**
 * Simple assistant reply function
 * In a real implementation, this would call an API or use AI
 */
export async function getBotReply(
    userMessage: string,
    mentionedPerson?: string,
    systemNotes?: SystemNote[]
): Promise<BotReply> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));

    const lowerMessage = userMessage.toLowerCase().trim();

    // Apply relevant system notes to enhance the response
    const relevantNotes = systemNotes?.filter(note => {
        const noteKeywords = [
            ...note.summary.toLowerCase().split(' '),
            ...note.question.toLowerCase().split(' '),
        ];
        const questionWords = lowerMessage.split(' ');
        return questionWords.some(word =>
            word.length > 3 && noteKeywords.some(keyword => keyword.includes(word) || word.includes(keyword))
        );
    }) || [];

    // Handle @mention questions (e.g., "@raza, does this make sense to you?")
    if (mentionedPerson) {
        const personName = mentionedPerson.charAt(0).toUpperCase() + mentionedPerson.slice(1).toLowerCase();

        // Special handler for Raza's policy validation question
        if (personName.toLowerCase() === 'raza' &&
            (lowerMessage.includes('double-check') || lowerMessage.includes('double check')) &&
            lowerMessage.includes('policy') &&
            (lowerMessage.includes('valid') || lowerMessage.includes('production'))) {

            const validationContent = `**Validation from Raza - Product Owner**

• The policy summary is accurate and remains valid for current production deployments
• Production AI API usage also requires cost approval for workloads exceeding defined thresholds
• A limited number of teams are operating under temporary exceptions pending Q4 review
• No changes have been approved to data handling or external model restrictions

**Knowledge Scope:** Owner-verified sources (Raza)`;

            return {
                content: validationContent,
                sources: [
                    {
                        id: 'src-1',
                        type: 'onedrive',
                        title: "Raza's OneDrive - Q3 AI Rollout Notes",
                        url: 'https://adnoc-my.sharepoint.com/personal/raza/Documents/q3-ai-rollout-notes',
                        description: "Raza's personal notes on Q3 AI rollout"
                    },
                    {
                        id: 'src-2',
                        type: 'sharepoint',
                        title: "Raza's SharePoint - Product Steering Updates",
                        url: 'https://adnoc.sharepoint.com/sites/raza/product-steering-updates',
                        description: "Raza's product steering documentation"
                    },
                    {
                        id: 'src-3',
                        type: 'teams',
                        title: "Microsoft Teams - Security Review Thread (Raza)",
                        url: 'https://teams.microsoft.com/l/channel/19:security-review-thread-raza',
                        description: 'Security review thread with Raza'
                    },
                    {
                        id: 'src-4',
                        type: 'human-assistant',
                        title: "Raza's Digital Assistant",
                        url: 'https://teams.microsoft.com/l/assistant/19:raza-assistant',
                        description: 'Response from Raza\'s digital assistant assistant'
                    }
                ],
                confidenceLevel: 92,
                systemNotesUsed: relevantNotes.length > 0 ? relevantNotes : undefined
            };
        }

        // Special handler for Raza's API rollout acceleration question (strategic decision)
        if (personName.toLowerCase() === 'raza' &&
            (lowerMessage.includes('accelerate') || lowerMessage.includes('can we accelerate')) &&
            (lowerMessage.includes('api rollout') || lowerMessage.includes('api')) &&
            lowerMessage.includes('q3') &&
            (lowerMessage.includes('q4') || lowerMessage.includes('instead of'))) {

            const decisionContent = `**Decision Requires Human Alignment**

I can't confirm this change confidently.

Accelerating the API rollout timeline would affect multiple teams, delivery plans, and agreed timelines. This decision is not defined in published policy or owner-verified documentation.

To avoid speculative or incorrect guidance, this request has been routed for human review.`;

            return {
                content: decisionContent,
                sources: [],
                confidenceLevel: undefined, // Not applicable for decision-level questions
                humanNotified: true,
                notifiedPerson: personName,
                followUpActions: [
                    {
                        id: 'action-1',
                        label: `Contact ${personName} directly`,
                        href: `https://teams.microsoft.com/l/notification/${personName.toLowerCase()}-direct-message`
                    },
                    {
                        id: 'action-2',
                        label: `Check for updates from ${personName}`,
                        onClick: () => {
                            console.log(`Check for updates from ${personName}`);
                        }
                    }
                ],
                systemNotesUsed: relevantNotes.length > 0 ? relevantNotes : undefined
            };
        }

        // Check if this is a question the assistant can't answer confidently
        // Examples: API targets, Q3/Q4 timing changes, strategic decisions
        const isLowConfidenceQuestion =
            lowerMessage.includes('api target') ||
            lowerMessage.includes('q3') ||
            lowerMessage.includes('q4') ||
            lowerMessage.includes('quarter') ||
            lowerMessage.includes('what would it take') ||
            lowerMessage.includes('how can we') ||
            lowerMessage.includes('can we hit') ||
            lowerMessage.includes('instead of');

        if (isLowConfidenceQuestion) {
            return {
                content: `Based on available information, I can't answer this question confidently. This requires strategic decision-making and detailed analysis that goes beyond my current knowledge base.\n\n**${personName} has been notified to answer you directly.**`,
                sources: [
                    {
                        id: 'src-1',
                        type: 'human-assistant',
                        title: `${personName}'s Digital Assistant`,
                        url: `https://teams.microsoft.com/l/assistant/19:${personName.toLowerCase()}-assistant`,
                        description: `Response from ${personName}'s digital assistant assistant`
                    }
                ],
                confidenceLevel: 35,
                humanNotified: true,
                notifiedPerson: personName,
                followUpActions: [
                    {
                        id: 'action-1',
                        label: `Contact ${personName} directly.`,
                        href: `https://teams.microsoft.com/l/notification/${personName.toLowerCase()}-direct-message`
                    },
                    {
                        id: 'action-2',
                        label: `Check for updates from ${personName}`,
                        onClick: () => {
                            console.log(`Check for updates from ${personName}`);
                        }
                    }
                ],
                systemNotesUsed: relevantNotes.length > 0 ? relevantNotes : undefined
            };
        }

        // Generate response from the mentioned person's assistant (high confidence)
        // Apply system notes if available
        let responseContent = `Yes, this makes sense to me. The AI KPIs for 2026 are well-aligned with our organizational goals. I particularly agree with the focus on productivity metrics and governance controls. The 20-30% reduction target for knowledge retrieval time is ambitious but achievable with the right implementation strategy.`;

        // Enhance response based on relevant system notes
        const teamsNote = relevantNotes.find(note => note.summary.toLowerCase().includes('teams'));
        if (teamsNote && (lowerMessage.includes('q3') || lowerMessage.includes('q4') || lowerMessage.includes('api target'))) {
            responseContent += `\n\n**Additional Context from Teams:**\n\nBased on recent Teams discussions, the team has been actively working on alignment and there's strong consensus on the approach.`;
        }

        return {
            content: responseContent,
            sources: [
                {
                    id: 'src-1',
                    type: 'sharepoint',
                    title: `${personName}'s Documents → Strategic Review 2026`,
                    url: `https://adnoc.sharepoint.com/sites/${personName.toLowerCase()}/strategic-review-2026`,
                    description: `${personName}'s strategic planning documents`
                },
                {
                    id: 'src-2',
                    type: 'onedrive',
                    title: `${personName}'s OneDrive → KPI Analysis.docx`,
                    url: `https://adnoc-my.sharepoint.com/personal/${personName.toLowerCase()}/Documents/kpi-analysis.docx`,
                    description: `${personName}'s personal analysis document`
                },
                {
                    id: 'src-3',
                    type: 'email',
                    title: `Email from ${personName} → KPI Discussion`,
                    url: `https://outlook.office.com/mail/id/${personName.toLowerCase()}-kpi-discussion`,
                    description: `Email conversation with ${personName}`
                },
                {
                    id: 'src-4',
                    type: 'human-assistant',
                    title: `${personName}'s Digital Assistant`,
                    url: `https://teams.microsoft.com/l/assistant/19:${personName.toLowerCase()}-assistant`,
                    description: `Response from ${personName}'s digital assistant assistant`
                }
            ],
            confidenceLevel: 88,
            systemNotesUsed: relevantNotes.length > 0 ? relevantNotes : undefined
        };
    }

    // AI API Usage Policy question handler
    if (
        lowerMessage.includes('ai api usage') ||
        lowerMessage.includes('ai api policy') ||
        lowerMessage.includes('approving ai api') ||
        (lowerMessage.includes('ai api') && (lowerMessage.includes('production') || lowerMessage.includes('policy') || lowerMessage.includes('approval'))) ||
        (lowerMessage.includes('policy') && lowerMessage.includes('ai api') && lowerMessage.includes('production'))
    ) {
        const policyContent = `**AI API Usage Policy - Production (Organization-Level)**

• AI APIs may be used in production environments under approved AI governance and security policies
• Requests require review and sign-off from Product, Engineering, and Information Security
• Sensitive, regulated, or proprietary data may not be sent to external models without explicit exception approval
• All API usage must support audit logging, access controls, and source traceability
• Periodic reviews are required to ensure continued compliance

**Knowledge Scope:** Organization-published policies`;

        return {
            content: policyContent,
            sources: [
                {
                    id: 'src-1',
                    type: 'sharepoint',
                    title: 'SharePoint - AI Governance Policy',
                    url: 'https://adnoc.sharepoint.com/sites/governance/ai-governance-policy',
                    description: 'Organization-published AI governance policy'
                },
                {
                    id: 'src-2',
                    type: 'sharepoint',
                    title: 'SharePoint - Security & Compliance Guidelines',
                    url: 'https://adnoc.sharepoint.com/sites/security/compliance-guidelines',
                    description: 'Organization-published security and compliance guidelines'
                },
                {
                    id: 'src-3',
                    type: 'onedrive',
                    title: 'OneDrive - Production Readiness Checklist',
                    url: 'https://adnoc-my.sharepoint.com/personal/governance/Documents/production-readiness-checklist',
                    description: 'Organization-published production readiness checklist'
                }
            ],
            confidenceLevel: 76,
            followUpActions: [
                {
                    id: 'action-1',
                    label: 'Request validation from @Raza — Medium confidence requires policy verification',
                    onClick: () => {
                        console.log('Request validation from Raza');
                    }
                }
            ]
        };
    }

    // Simple pattern matching for demo
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
        return {
            content: "Hello! How can I assist you today?",
            sources: [
                {
                    id: 'src-1',
                    type: 'assistant',
                    title: 'User Assistant Knowledge Base',
                    url: '#',
                    description: 'General greeting response'
                }
            ]
        };
    }

    if (lowerMessage.includes('help')) {
        return {
            content: "I'm here to help! You can ask me questions about:\n\n• Project information\n• Technical details\n• General assistance\n\nWhat would you like to know?",
            sources: [
                {
                    id: 'src-1',
                    type: 'sharepoint',
                    title: 'Help Documentation - SharePoint',
                    url: 'https://adnoc.sharepoint.com/sites/help/docs',
                    description: 'Official help documentation'
                },
                {
                    id: 'src-2',
                    type: 'assistant',
                    title: 'Assistant Knowledge Base',
                    url: '#',
                    description: 'Built-in help responses'
                }
            ]
        };
    }

    if (lowerMessage.includes('thank')) {
        return {
            content: "You're welcome! Is there anything else I can help you with?",
            sources: []
        };
    }

    if (lowerMessage.includes('bye') || lowerMessage.includes('goodbye')) {
        return {
            content: "Goodbye! Feel free to come back if you have more questions.",
            sources: []
        };
    }

    // Default response with sample sources
    const sources: MessageSource[] = [
        {
            id: 'src-1',
            type: 'sharepoint',
            title: 'Project Documentation - SharePoint',
            url: 'https://adnoc.sharepoint.com/sites/projects/docs',
            description: 'Project-related information'
        },
        {
            id: 'src-2',
            type: 'onedrive',
            title: 'Technical Specifications.docx',
            url: 'https://adnoc-my.sharepoint.com/personal/user/Documents/tech-specs.docx',
            description: 'Technical documentation from OneDrive'
        },
        {
            id: 'src-3',
            type: 'email',
            title: 'Email from Jack - Project Update',
            url: 'https://outlook.office.com/mail/id/12345',
            description: 'Recent email conversation'
        }
    ];

    // Add more sources based on keywords
    if (lowerMessage.includes('project') || lowerMessage.includes('pmo')) {
        sources.push({
            id: 'src-4',
            type: 'teams',
            title: 'PMO Team Chat - Teams',
            url: 'https://teams.microsoft.com/l/channel/19:project-discussion',
            description: 'Team discussion thread'
        });
    }

    if (lowerMessage.includes('finance') || lowerMessage.includes('budget')) {
        sources.push({
            id: 'src-5',
            type: 'sharepoint',
            title: 'Finance Reports - SharePoint',
            url: 'https://adnoc.sharepoint.com/sites/finance/reports',
            description: 'Financial documentation'
        });
    }

    // Apply system notes to default response if available
    let defaultContent = `I understand you're asking about "${userMessage}". Let me help you with that. Based on the available information, here's what I found.`;

    if (relevantNotes.length > 0) {
        const note = relevantNotes[0]; // Use the most relevant note
        if (note.summary.toLowerCase().includes('detail') || note.summary.toLowerCase().includes('context')) {
            defaultContent += `\n\n**Additional Context:**\n\n${note.summary}`;
        }
    }

    return {
        content: defaultContent,
        sources,
        systemNotesUsed: relevantNotes.length > 0 ? relevantNotes : undefined
    };
}

/**
 * Generate a draft response for a question that requires human review
 * This is used in the interactions view to show a suggested response
 */
export function generateDraftResponse(question: string, mentionedPerson?: string): string {
    const lowerQuestion = question.toLowerCase().trim();

    // Check for engineering requirements question with mentioned person
    if ((lowerQuestion.includes('engineering requirements') || lowerQuestion.includes('engineering requirement')) &&
        (lowerQuestion.includes('integrating') || lowerQuestion.includes('integration')) &&
        (lowerQuestion.includes('product') || lowerQuestion.includes('new product')) &&
        mentionedPerson) {
        return `**Engineering Integration Requirements (Platform-Level)**

New product features must align with approved platform APIs and service contracts

Security, performance, and reliability reviews are required before production release

Feature work must comply with platform versioning and deployment guidelines

Integration timelines depend on existing roadmap commitments and capacity

**Additional context from ${mentionedPerson}:**

Platform capacity is currently constrained due to ongoing roadmap commitments

New integrations are being prioritized based on dependency impact and delivery risk

Early alignment with platform engineers helps reduce downstream delays

**Knowledge Scope:** Owner-verified engineering context (${mentionedPerson}) and organization-published engineering standards
**Confidence:** High (90%)`;
    }

    // Generate contextual draft responses based on the question
    // Check for API rollout acceleration question
    if ((lowerQuestion.includes('accelerate') || lowerQuestion.includes('can we accelerate')) &&
        (lowerQuestion.includes('api rollout') || lowerQuestion.includes('api')) &&
        lowerQuestion.includes('q3') &&
        (lowerQuestion.includes('q4') || lowerQuestion.includes('instead of'))) {
        return `Based on current planning assumptions, accelerating the API rollout from Q4 to Q3 may be possible, but it would require changes to the existing delivery plan.

**Key factors to consider:**

• The current Q4 timeline reflects planned capacity and sequencing across teams

• Moving to a Q3 rollout would require reprioritization of engineering and platform work

• Dependencies related to security validation, cost approval, and readiness would need to be addressed earlier

• Alignment across impacted teams would be necessary before adjusting timelines

• Further review would be recommended to confirm feasibility and assess potential risks.`;
    }

    if (lowerQuestion.includes('api target') || lowerQuestion.includes('q3') || lowerQuestion.includes('q4')) {
        return `Based on our current progress and resource allocation, I believe we can achieve the API targets for Q3/Q4. However, this will require:\n\n1. Accelerated development timeline\n2. Additional resources in the engineering team\n3. Close coordination with stakeholders\n\nI recommend we schedule a review meeting to discuss the feasibility and any potential blockers.`;
    }

    if (lowerQuestion.includes('what would it take') || lowerQuestion.includes('how can we')) {
        return `To accomplish this, we would need:\n\n• Clear definition of success criteria\n• Resource allocation and timeline approval\n• Stakeholder alignment\n• Risk assessment and mitigation plans\n\nI'm happy to discuss the details further if you'd like to schedule a call.`;
    }

    if (lowerQuestion.includes('can we hit') || lowerQuestion.includes('instead of')) {
        return `Yes, I believe we can hit this target, but it will require some adjustments:\n\n• Reprioritization of current initiatives\n• Additional support from cross-functional teams\n• Extended timeline or increased resources\n\nLet me know if you'd like me to prepare a detailed plan.`;
    }

    if (lowerQuestion.includes('ai api usage') || lowerQuestion.includes('ai api policy') ||
        (lowerQuestion.includes('ai api') && (lowerQuestion.includes('production') || lowerQuestion.includes('policy') || lowerQuestion.includes('approval')))) {
        return `**AI API Usage Policy - Production (Organization-Level)**\n\n• AI APIs may be used in production environments under approved AI governance and security policies\n• Requests require review and sign-off from Product, Engineering, and Information Security\n• Sensitive, regulated, or proprietary data may not be sent to external models without explicit exception approval\n• All API usage must support audit logging, access controls, and source traceability\n• Periodic reviews are required to ensure continued compliance\n\n**Knowledge Scope:** Organization-published policies`;
    }

    if (lowerQuestion.includes('kpi') || lowerQuestion.includes('key performance indicator')) {
        return `The KPIs are well-defined and aligned with our strategic goals. Based on current metrics:\n\n• We're on track for productivity targets\n• Adoption rates are meeting expectations\n• Governance controls are functioning as designed\n\nI can provide a detailed breakdown if needed.`;
    }

    // Default draft response
    return `Thank you for your question. Based on my analysis:\n\n• This requires careful consideration of multiple factors\n• I recommend we discuss this in detail to ensure alignment\n• I can provide more specific guidance after reviewing the context\n\nWould you like to schedule a follow-up discussion?`;
}

/**
 * Generate a draft response with instructions incorporated
 * This simulates the assistant regenerating a response based on user feedback
 */
export function generateDraftWithInstructions(
    question: string,
    originalDraft: string,
    instructions: string,
    mentionedPerson?: string
): string {
    const lowerInstructions = instructions.toLowerCase();
    const lowerQuestion = question.toLowerCase();

    // Check what type of instruction was given
    const isTeamsInstruction = lowerInstructions.includes('teams') || lowerInstructions.includes('chat');
    const isRewriteInstruction = lowerInstructions.includes('rewrite') || lowerInstructions.includes('rewritten');
    const isMoreDetailInstruction = lowerInstructions.includes('detail') || lowerInstructions.includes('more context') || lowerInstructions.includes('elaborate');
    const isQ3Instruction = lowerInstructions.includes('q3') || lowerQuestion.includes('q3');
    const isQ4Instruction = lowerInstructions.includes('q4') || lowerQuestion.includes('q4');

    // Generate enhanced response based on instructions
    // Check for Q3/Q4 Planning Discussion and rollout notes instruction
    const isQ3Q4PlanningInstruction = (lowerInstructions.includes('q3/q4') || (lowerInstructions.includes('q3') && lowerInstructions.includes('q4'))) &&
        (lowerInstructions.includes('planning discussion') || lowerInstructions.includes('project-updates')) &&
        (lowerInstructions.includes('rollout notes') || lowerInstructions.includes('rollout'));

    if (isQ3Q4PlanningInstruction && (isRewriteInstruction || lowerInstructions.includes('update'))) {
        return `Based on current planning discussions and recent rollout updates, accelerating the API rollout from Q4 to Q3 may be feasible under specific conditions, but it is not a confirmed plan.

**Key considerations:**

• The current Q4 timeline accounts for capacity constraints across engineering and platform teams

• An accelerated timeline would require reprioritization of existing commitments and additional delivery focus

• Dependencies related to security validation and cost approval would need to be resolved earlier than planned

• Alignment across affected teams would be required before adjusting the rollout schedule

• A decision to change the rollout timeline should follow a focused review to assess feasibility, risks, and impact on existing priorities.`;
    }

    if (isTeamsInstruction && isRewriteInstruction) {
        return `Based on the Teams chat discussions and recent updates, here's a more comprehensive response:\n\n${originalDraft}\n\n**Additional Context from Teams:**\n\n• Recent team discussions indicate strong alignment on the proposed approach\n• Stakeholders have expressed support for the timeline adjustments\n• The team has identified key dependencies that need to be addressed\n• There's consensus on prioritizing the most critical deliverables first\n\nI've incorporated insights from the Teams conversations to provide a more complete picture.`;
    }

    if (isMoreDetailInstruction && (isQ3Instruction || isQ4Instruction)) {
        const quarter = isQ3Instruction ? 'Q3' : 'Q4';
        return `Here's a more detailed response with additional context about ${quarter}:\n\n${originalDraft}\n\n**${quarter} Specific Details:**\n\n• Current progress tracking shows we're at 65% completion for ${quarter} milestones\n• Resource allocation has been adjusted to prioritize critical path items\n• Key stakeholders have been briefed and are aligned with the updated timeline\n• Risk mitigation plans are in place for identified bottlenecks\n• Weekly status reviews are scheduled to ensure we stay on track\n\nThe response now includes more granular information about ${quarter} planning and execution.`;
    }

    if (isMoreDetailInstruction) {
        return `${originalDraft}\n\n**Additional Context:**\n\n• Detailed analysis shows multiple factors contributing to this situation\n• Historical data indicates similar patterns with successful resolution strategies\n• Current metrics demonstrate progress in the right direction\n• Stakeholder feedback has been positive regarding the proposed approach\n• Next steps have been clearly defined with ownership and timelines\n\nI've expanded the response with more comprehensive details and context.`;
    }

    if (isRewriteInstruction) {
        return `Here's a revised response incorporating your feedback:\n\n${originalDraft}\n\n**Revised Analysis:**\n\nThe approach has been refined based on the additional context. Key considerations include:\n\n• Updated assessment of current state and requirements\n• Revised timeline and resource allocation strategy\n• Enhanced risk mitigation approach\n• Clearer communication of next steps and expectations\n\nThe response has been rewritten to better address the question with improved clarity and detail.`;
    }

    // Default: enhance the original draft with instruction context
    return `${originalDraft}\n\n**Additional Context:**\n\n• Detailed analysis shows multiple factors contributing to this situation\n• Historical data indicates similar patterns with successful resolution strategies\n• Current metrics demonstrate progress in the right direction\n• Stakeholder feedback has been positive regarding the proposed approach\n• Next steps have been clearly defined with ownership and timelines\n\nI've expanded the response with more comprehensive details and context.`;
}

/**
 * Get initial sources for a draft response
 * This provides sources that would be used for the first draft
 */
export function getInitialDraftSources(question: string, mentionedPerson?: string): MessageSource[] {
    const lowerQuestion = question.toLowerCase();
    const personName = mentionedPerson ? mentionedPerson.charAt(0).toUpperCase() + mentionedPerson.slice(1).toLowerCase() : 'User';

    const sources: MessageSource[] = [];

    // Check for engineering requirements question with mentioned person
    if ((lowerQuestion.includes('engineering requirements') || lowerQuestion.includes('engineering requirement')) &&
        (lowerQuestion.includes('integrating') || lowerQuestion.includes('integration')) &&
        (lowerQuestion.includes('product') || lowerQuestion.includes('new product')) &&
        mentionedPerson) {
        sources.push({
            id: 'draft-src-1',
            type: 'onedrive',
            title: `${personName}'s OneDrive – Platform Roadmap Notes`,
            url: `https://adnoc-my.sharepoint.com/personal/${personName.toLowerCase()}/Documents/platform-roadmap`,
            description: `${personName}'s platform roadmap notes`
        });

        sources.push({
            id: 'draft-src-2',
            type: 'sharepoint',
            title: 'SharePoint – Engineering Integration Guidelines',
            url: 'https://adnoc.sharepoint.com/sites/engineering/integration-guidelines',
            description: 'Organization-published engineering integration guidelines'
        });

        sources.push({
            id: 'draft-src-3',
            type: 'sharepoint',
            title: 'SharePoint – Engineering Planning Updates',
            url: 'https://adnoc.sharepoint.com/sites/engineering/planning-updates',
            description: 'Engineering planning updates'
        });

        sources.push({
            id: 'draft-src-4',
            type: 'onedrive',
            title: 'OneDrive – Platform API Standards',
            url: 'https://adnoc-my.sharepoint.com/personal/engineering/Documents/platform-api-standards',
            description: 'Organization-published platform API standards'
        });

        return sources;
    }

    // Check for API rollout acceleration question - only knowledge base sources (no communication channels)
    if ((lowerQuestion.includes('accelerate') || lowerQuestion.includes('can we accelerate')) &&
        (lowerQuestion.includes('api rollout') || lowerQuestion.includes('api')) &&
        lowerQuestion.includes('q3') &&
        (lowerQuestion.includes('q4') || lowerQuestion.includes('instead of'))) {
        sources.push({
            id: 'draft-src-1',
            type: 'sharepoint',
            title: 'Strategy → Quarterly Planning Documents',
            url: 'https://adnoc.sharepoint.com/sites/strategy/quarterly-planning',
            description: 'Strategic planning documents for quarterly targets'
        });

        sources.push({
            id: 'draft-src-2',
            type: 'onedrive',
            title: 'OneDrive → Delivery Plan Documentation',
            url: 'https://adnoc-my.sharepoint.com/personal/governance/Documents/delivery-plan',
            description: 'Delivery plan documentation'
        });
    }
    // Add sources based on question type (general Q3/Q4 - knowledge base only, no Teams)
    else if (lowerQuestion.includes('api target') || lowerQuestion.includes('q3') || lowerQuestion.includes('q4')) {
        sources.push({
            id: 'draft-src-1',
            type: 'sharepoint',
            title: 'Strategy → Quarterly Planning Documents',
            url: 'https://adnoc.sharepoint.com/sites/strategy/quarterly-planning',
            description: 'Strategic planning documents for quarterly targets'
        });
    }

    if (lowerQuestion.includes('ai api usage') || lowerQuestion.includes('ai api policy') ||
        (lowerQuestion.includes('ai api') && (lowerQuestion.includes('production') || lowerQuestion.includes('policy') || lowerQuestion.includes('approval')))) {
        sources.push({
            id: 'draft-src-3',
            type: 'sharepoint',
            title: 'SharePoint - AI Governance Policy',
            url: 'https://adnoc.sharepoint.com/sites/governance/ai-governance-policy',
            description: 'Organization-published AI governance policy'
        });

        sources.push({
            id: 'draft-src-4',
            type: 'sharepoint',
            title: 'SharePoint - Security & Compliance Guidelines',
            url: 'https://adnoc.sharepoint.com/sites/security/compliance-guidelines',
            description: 'Organization-published security and compliance guidelines'
        });

        sources.push({
            id: 'draft-src-5',
            type: 'onedrive',
            title: 'OneDrive - Production Readiness Checklist',
            url: 'https://adnoc-my.sharepoint.com/personal/governance/Documents/production-readiness-checklist',
            description: 'Organization-published production readiness checklist'
        });
    }

    if (lowerQuestion.includes('kpi') || lowerQuestion.includes('key performance indicator')) {
        sources.push({
            id: 'draft-src-3',
            type: 'sharepoint',
            title: 'Strategy → AI Roadmap 2026',
            url: 'https://adnoc.sharepoint.com/sites/strategy/ai-roadmap-2026',
            description: 'Approved strategic planning document'
        });

        sources.push({
            id: 'draft-src-4',
            type: 'sharepoint',
            title: 'PMO → Digital Transformation KPIs',
            url: 'https://adnoc.sharepoint.com/sites/pmo/digital-transformation-kpis',
            description: 'PMO KPI documentation'
        });
    }

    if (mentionedPerson) {
        sources.push({
            id: 'draft-src-5',
            type: 'human-assistant',
            title: `${personName}'s Digital Assistant`,
            url: `https://teams.microsoft.com/l/assistant/19:${personName.toLowerCase()}-assistant`,
            description: `Response from ${personName}'s digital assistant assistant`
        });

        sources.push({
            id: 'draft-src-6',
            type: 'sharepoint',
            title: `${personName}'s Documents → Strategic Review 2026`,
            url: `https://adnoc.sharepoint.com/sites/${personName.toLowerCase()}/strategic-review-2026`,
            description: `${personName}'s strategic planning documents`
        });
    }

    // Default sources
    if (sources.length === 0) {
        sources.push({
            id: 'draft-src-7',
            type: 'sharepoint',
            title: 'Project Documentation - SharePoint',
            url: 'https://adnoc.sharepoint.com/sites/projects/docs',
            description: 'Project-related information'
        });

        sources.push({
            id: 'draft-src-8',
            type: 'onedrive',
            title: 'Technical Specifications.docx',
            url: 'https://adnoc-my.sharepoint.com/personal/user/Documents/tech-specs.docx',
            description: 'Technical documentation from OneDrive'
        });
    }

    return sources;
}

/**
 * Get sources that show feedback was incorporated
 * This simulates the assistant showing which sources were used after incorporating instructions
 */
export function getSourcesWithFeedback(
    question: string,
    instructions: string,
    mentionedPerson?: string
): MessageSource[] {
    const lowerInstructions = instructions.toLowerCase();
    const lowerQuestion = question.toLowerCase();
    const personName = mentionedPerson ? mentionedPerson.charAt(0).toUpperCase() + mentionedPerson.slice(1).toLowerCase() : 'User';

    const sources: MessageSource[] = [];

    // Add Teams source if instructions mention Teams
    if (lowerInstructions.includes('teams') || lowerInstructions.includes('chat')) {
        // Check for Q3/Q4 Planning Discussion and rollout notes
        const isQ3Q4PlanningInstruction = (lowerInstructions.includes('q3/q4') || (lowerInstructions.includes('q3') && lowerInstructions.includes('q4'))) &&
            (lowerInstructions.includes('planning discussion') || lowerInstructions.includes('project-updates')) &&
            (lowerInstructions.includes('rollout notes') || lowerInstructions.includes('rollout'));

        if (isQ3Q4PlanningInstruction) {
            sources.push({
                id: 'feedback-src-1',
                type: 'teams',
                title: '#project-updates → Q3/Q4 Planning Discussion',
                url: 'https://teams.microsoft.com/l/channel/19:project-updates/q3-q4-planning',
                description: 'Q3/Q4 Planning Discussion referenced per feedback instructions'
            });

            sources.push({
                id: 'feedback-src-2',
                type: 'onedrive',
                title: "Raza's OneDrive - Recent Rollout Notes",
                url: 'https://adnoc-my.sharepoint.com/personal/raza/Documents/recent-rollout-notes',
                description: 'Recent rollout notes referenced per feedback instructions'
            });
        } else {
            sources.push({
                id: 'feedback-src-1',
                type: 'teams',
                title: '#ai-governance → Recent Discussion Thread (Feedback Incorporated)',
                url: 'https://teams.microsoft.com/l/channel/19:ai-governance/recent-discussion',
                description: 'Teams chat discussion used to enhance response based on feedback'
            });

            sources.push({
                id: 'feedback-src-2',
                type: 'teams',
                title: '#project-updates → Q3/Q4 Planning Discussion',
                url: 'https://teams.microsoft.com/l/channel/19:project-updates/q3-q4-planning',
                description: 'Team planning discussions referenced per feedback instructions'
            });
        }
    }

    // Add SharePoint sources for detailed context
    if (lowerInstructions.includes('detail') || lowerInstructions.includes('context')) {
        sources.push({
            id: 'feedback-src-3',
            type: 'sharepoint',
            title: 'Strategy → Detailed Analysis Documents (Feedback Incorporated)',
            url: 'https://adnoc.sharepoint.com/sites/strategy/detailed-analysis',
            description: 'Detailed documents reviewed based on feedback to enhance response'
        });
    }

    // Add source showing feedback was incorporated
    sources.push({
        id: 'feedback-src-4',
        type: 'human-assistant',
        title: `${personName}'s Digital Assistant → Feedback Incorporated`,
        url: `https://teams.microsoft.com/l/assistant/19:${personName.toLowerCase()}-assistant/feedback`,
        description: 'Response regenerated with user feedback and instructions incorporated'
    });

    // Add email source if Q3/Q4 mentioned
    if (lowerQuestion.includes('q3') || lowerQuestion.includes('q4') || lowerInstructions.includes('q3') || lowerInstructions.includes('q4')) {
        sources.push({
            id: 'feedback-src-5',
            type: 'email',
            title: `Email Thread → ${personName} - Quarterly Planning Discussion`,
            url: `https://outlook.office.com/mail/id/${personName.toLowerCase()}-quarterly-planning`,
            description: 'Email discussions referenced to provide more context'
        });
    }

    // Always add a source indicating feedback was used
    if (sources.length === 1) {
        sources.push({
            id: 'feedback-src-6',
            type: 'sharepoint',
            title: 'Knowledge Base → Enhanced Response (Feedback Applied)',
            url: 'https://adnoc.sharepoint.com/sites/knowledge-base/enhanced-response',
            description: 'Additional sources reviewed based on user instructions'
        });
    }

    return sources;
}

/**
 * Generate a better title from a question or message
 * Cleans up the text, removes mentions, and creates a readable title
 */
export function generateTitleFromQuestion(question: string, maxLength: number = 60): string {
    if (!question || question.trim().length === 0) {
        return 'New conversation';
    }

    // Remove @mentions (e.g., "@raza" or "@raza,")
    let cleaned = question.replace(/@\w+\s*,?\s*/gi, '').trim();

    // Remove leading question words if they make the title too long
    // But keep them if the question is short
    const questionWords = /^(what|who|when|where|why|how|can|could|would|should|is|are|do|does|did)\s+/i;
    if (cleaned.length > maxLength && cleaned.match(questionWords)) {
        cleaned = cleaned.replace(questionWords, '').trim();
    }

    // Capitalize first letter
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);

    // Truncate at word boundary if too long
    if (cleaned.length > maxLength) {
        const truncated = cleaned.substring(0, maxLength);
        const lastSpace = truncated.lastIndexOf(' ');
        if (lastSpace > maxLength * 0.7) { // Only truncate at word if we're not losing too much
            cleaned = truncated.substring(0, lastSpace) + '...';
        } else {
            cleaned = truncated + '...';
        }
    }

    // Remove trailing punctuation if it's just a question mark or period
    cleaned = cleaned.replace(/[.?!]+$/, '');

    return cleaned || 'New conversation';
}

